import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SpeedIcon from '@mui/icons-material/Speed';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ErrorIcon from '@mui/icons-material/Error';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { chartTooltipProps } from '@/lib/chartTheme';
import type { AgentPerformanceData, AgentPerformanceTrend } from '@/types';
import { dateFnsLocale, formatNumber } from '@/utils/locale';
import {
  formatChartValue,
  formatMeasured,
  hasAgentPerformanceData,
  hasPlottablePoint,
  isMeasured,
} from '../reportMeasurements';
import { CompactNoData, ReportSectionShell } from './ReportSectionShell';

interface AgentPerformanceSectionProps {
  agentPerformanceData: AgentPerformanceData;
  agentPerformanceTrend: AgentPerformanceTrend[];
  isMobile: boolean;
  colors: string[];
}

export function AgentPerformanceSection({
  agentPerformanceData,
  agentPerformanceTrend,
  isMobile,
  colors,
}: AgentPerformanceSectionProps) {
  const { t } = useTranslation('reports');
  const theme = useTheme();

  // A period without any agent evidence is one compact line, not zero cards and empty charts.
  if (!hasAgentPerformanceData(agentPerformanceData, agentPerformanceTrend)) {
    return <CompactNoData title={t('agentPerformance')} testId="agent-performance-section" />;
  }

  const {
    totalInteractions,
    performanceChange,
    intentCoverage,
    intentAccuracy,
    avgResponseTime,
    handoffRate,
    handoffCoverage,
    agentBreakdown,
    intentDistribution,
    topErrors,
  } = agentPerformanceData;
  // Why the handoff rate is missing: no conversation of the cohort has a session record, or
  // only some do. A rate over part of the cohort would be a guess, so none is shown.
  const handoffNote = isMeasured(handoffRate)
    ? t('requiresHumanHelp')
    : handoffCoverage.conversationsWithSession > 0
      ? t('handoffPartialCoverage', {
          covered: handoffCoverage.conversationsWithSession,
          eligible: handoffCoverage.eligibleConversations,
        })
      : handoffCoverage.eligibleConversations > 0
        ? t('handoffNoSessions')
        : t('notMeasured');
  // Per-intent success is not recorded; show the column only if a row carries a figure.
  const showIntentSuccess = intentDistribution.some((intent) => isMeasured(intent.successRate));
  const showTrend = hasPlottablePoint(agentPerformanceTrend, ['successRate', 'avgResponseTime']);
  // Volume without any LLM turn means latency and success were never recorded, which is
  // not the same as an agent that answers in 0 s and always fails.
  const volumeWithoutLlm = totalInteractions > 0 && !isMeasured(avgResponseTime);

  return (
    <ReportSectionShell title={t('agentPerformance')} testId="agent-performance-section">
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, border: 1, borderColor: 'primary.200', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SmartToyIcon sx={{ color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary">
                {t('interactions')}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} color="primary.main" data-testid="agent-interactions">
              {formatNumber(totalInteractions)}
            </Typography>
            {isMeasured(performanceChange) && performanceChange > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" color="success.main">
                  +{performanceChange.toFixed(1)}% {t('vsPrevious')}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2, border: 1, borderColor: 'success.200', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PsychologyIcon sx={{ color: 'success.main' }} />
              <Typography variant="caption" color="text.secondary">
                {t('intentCoverage')}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} color="success.main" data-testid="agent-intent-coverage">
              {formatMeasured(intentCoverage, 1, '%')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isMeasured(intentCoverage) ? t('intentCoverageDesc') : t('notMeasured')}
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2, border: 1, borderColor: 'info.200', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SpeedIcon sx={{ color: 'info.main' }} />
              <Typography variant="caption" color="text.secondary">
                {t('responseTime')}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} color="info.main" data-testid="agent-response-time">
              {formatMeasured(avgResponseTime, 1, 's')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isMeasured(avgResponseTime) ? t('averageLabel') : t('notMeasured')}
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, border: 1, borderColor: 'warning.200', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonIcon sx={{ color: 'warning.main' }} />
              <Typography variant="caption" color="text.secondary">
                {t('handoffRate')}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} color="warning.main" data-testid="agent-handoff-rate">
              {formatMeasured(handoffRate, 1, '%')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {handoffNote}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        {volumeWithoutLlm && (
          <Typography variant="body2" color="text.secondary" data-testid="agent-no-llm-note">
            {t('agentNoLlmMeasurements')}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" data-testid="agent-accuracy-note">
          {isMeasured(intentAccuracy)
            ? `${t('accuracy')}: ${intentAccuracy.toFixed(1)}%`
            : t('accuracyNotMeasured')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {agentBreakdown.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('agentBreakdown')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('agent')}</TableCell>
                    <TableCell align="right">{t('interactionsCount')}</TableCell>
                    <TableCell align="right">{t('successRate')}</TableCell>
                    <TableCell align="right">{t('responseTime')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentBreakdown.map((agent) => (
                    <TableRow key={agent.agentType} hover>
                      <TableCell>
                        <Typography variant="body2">{agent.agentType}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={agent.interactions} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          color={isMeasured(agent.successRate) && agent.successRate >= 95 ? 'success.main' : 'text.primary'}
                          title={isMeasured(agent.successRate) ? undefined : t('notMeasured')}
                        >
                          {formatMeasured(agent.successRate, 1, '%')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          title={isMeasured(agent.avgResponseTime) ? undefined : t('notMeasured')}
                        >
                          {formatMeasured(agent.avgResponseTime, 1, 's')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {showTrend && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('performanceTrend')}
            </Typography>
            <Box sx={{ p: 2 }}>
              <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
                <LineChart
                  data={agentPerformanceTrend}
                  margin={isMobile ? { top: 5, right: 10, left: 0, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => format(new Date(value), 'd.M.', { locale: dateFnsLocale() })}
                  />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    {...chartTooltipProps(theme)}
                    labelFormatter={(value) => format(new Date(value as string), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    formatter={(value) => formatChartValue(value, t('notMeasured'))}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="successRate"
                    stroke={theme.palette.success.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                    name={t('successRate')}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="avgResponseTime"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                    name={t('responseTimeSeconds')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        )}

        {intentDistribution.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('intentDistribution')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('intent')}</TableCell>
                    <TableCell align="right">{t('count')}</TableCell>
                    {showIntentSuccess && <TableCell align="right">{t('successRate')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {intentDistribution.map((intent, index) => (
                    <TableRow key={intent.intent} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: colors[index % colors.length],
                            }}
                          />
                          <Typography variant="body2">{intent.intent}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={intent.count} size="small" variant="outlined" />
                      </TableCell>
                      {showIntentSuccess && (
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            title={isMeasured(intent.successRate) ? undefined : t('notMeasured')}
                          >
                            {formatMeasured(intent.successRate, 1, '%')}
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {topErrors.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />
              {t('topErrors')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  {topErrors.map((error, index) => (
                    <TableRow key={`${error.error}-${index}`} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'error.light' }}>
                            {index + 1}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{error.error}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {error.agentType}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={error.count} size="small" color="error" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>
    </ReportSectionShell>
  );
}
